// Bridge between React and the YouTube IFrame Player API.
// Provides a reliable, ready-state-aware way to control YouTube embeds:
//   - waits for the SDK to load (lazy, one-time)
//   - waits for the player onReady before accepting commands
//   - queues commands issued before ready and flushes on ready
//   - cleans up properly on unmount

let sdkPromise = null;

const loadSdk = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") {
        try {
          prev();
        } catch {
          /* ignore */
        }
      }
      resolve(window.YT);
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);
  });
  return sdkPromise;
};

const extractVideoId = (url) => {
  if (!url) return null;
  if (url.includes("/embed/")) {
    const m = url.match(/\/embed\/([\w-]{11})/);
    return m ? m[1] : null;
  }
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

/**
 * Create a YouTube player attached to a host DOM element.
 *
 * @param {HTMLElement} host  Container element (will be REPLACED by the YouTube iframe)
 * @param {string} url        Any YouTube URL — videoId extracted automatically
 * @param {object} opts
 *   - startMuted (bool): whether to start muted (default true for autoplay compatibility)
 *   - loop (bool): loop the video (default true)
 *   - onReady (fn): called when player is ready
 *   - onError (fn): called on player errors
 *
 * Returns a controller object: { setVolume(0-100), mute(), unMute(), destroy() }
 * setVolume/mute/unMute can be called BEFORE onReady — they'll be queued.
 */
export const createYouTubePlayer = (host, url, opts = {}) => {
  const videoId = extractVideoId(url);
  if (!host || !videoId) {
    return {
      setVolume: () => {},
      mute: () => {},
      unMute: () => {},
      destroy: () => {},
      isReady: () => false,
    };
  }

  let player = null;
  let ready = false;
  let destroyed = false;
  const queue = [];

  const flush = () => {
    while (queue.length > 0) {
      const action = queue.shift();
      try {
        action();
      } catch {
        /* ignore — player may have been destroyed mid-flush */
      }
    }
  };

  const exec = (action) => {
    if (ready && player) {
      try {
        action();
      } catch {
        /* ignore */
      }
    } else {
      queue.push(action);
    }
  };

  loadSdk()
    .then((YT) => {
      if (destroyed) return;
      player = new YT.Player(host, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: opts.startMuted === false ? 0 : 1,
          loop: 1,
          playlist: videoId, // required for loop=1 to actually loop
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            ready = true;
            // Ensure autoplay actually starts in some browsers
            try {
              e.target.playVideo();
            } catch {
              /* ignore */
            }
            flush();
            if (typeof opts.onReady === "function") opts.onReady(e.target);
          },
          onStateChange: (e) => {
            // If video ends and loop didn't pick up (some embed restrictions), restart.
            // BUT don't restart live streams — getDuration() === 0 signals live/ongoing.
            if (e.data === window.YT.PlayerState.ENDED) {
              try {
                const dur = e.target.getDuration ? e.target.getDuration() : 0;
                if (dur > 0) {
                  e.target.seekTo(0);
                  e.target.playVideo();
                }
              } catch {
                /* ignore */
              }
            }
          },
          onError: (e) => {
            // YouTube IFrame API error codes:
            //   2   - invalid parameter
            //   5   - HTML5 player error
            //   100 - video not found / made private
            //   101 - embed not allowed by owner ← typical of licensed live streams
            //   150 - same as 101 (cross-domain)
            const code = e.data;
            const friendly = (() => {
              if (code === 101 || code === 150) {
                return {
                  code,
                  kind: "embed-blocked",
                  message: "Esta transmissão não permite ser exibida fora do YouTube (restrição do criador).",
                };
              }
              if (code === 100) {
                return {
                  code,
                  kind: "not-found",
                  message: "Vídeo não encontrado ou tornado privado pelo criador.",
                };
              }
              if (code === 2) {
                return {
                  code,
                  kind: "invalid-url",
                  message: "URL do YouTube inválida.",
                };
              }
              return {
                code,
                kind: "unknown",
                message: "Erro ao carregar vídeo do YouTube.",
              };
            })();
            if (typeof opts.onError === "function") opts.onError(friendly);
          },
        },
      });
    })
    .catch(() => {
      /* SDK load failed — possibly blocked by firewall */
    });

  return {
    setVolume: (vol) => {
      const clamped = Math.max(0, Math.min(100, Math.round(vol)));
      exec(() => player.setVolume(clamped));
    },
    mute: () => exec(() => player.mute()),
    unMute: () => exec(() => player.unMute()),
    destroy: () => {
      destroyed = true;
      ready = false;
      queue.length = 0;
      if (player) {
        try {
          player.destroy();
        } catch {
          /* ignore */
        }
        player = null;
      }
    },
    isReady: () => ready,
  };
};
